import { useMemo, useState, useRef } from "react";
import { addDays, format } from "date-fns";
import { it } from "date-fns/locale";
import { useDroppable } from "@dnd-kit/core";
import { fmtWeekday, fmtDayNum, timeToMin, ISO } from "@/lib/date";
import { tasksForDate, findCategory, useStore, toggleTaskDone, updateTask, updateOccurrence, startTimer, stopTimer, toggleTaskPriority, prioritiesForDate, isTimerFor } from "@/lib/store";
import RecurrenceScopeDialog from "@/components/RecurrenceScopeDialog";
import { metallicConfetti } from "@/lib/confetti";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Check, Star, Play, Pause, GripVertical } from "lucide-react";
import { toast } from "sonner";
import TaskDialog from "@/components/TaskDialog";

function HalfHourSlot({ dateISO, h, m, onOpen, half }) {
  const time = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  const { setNodeRef, isOver } = useDroppable({
    id: `slot-${dateISO}-${time}`,
    data: { type: "slot", date: dateISO, time }
  });
  const posClass = half === "top" ? "absolute inset-0 top-0 h-1/2 w-full" : "absolute inset-x-0 bottom-0 h-1/2 w-full";
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={() => onOpen(dateISO, time)}
      className={`${posClass} transition-colors ${isOver ? "bg-[#A3B897]/25" : "hover:bg-[#FDFDFB]"}`}
      aria-label={`Sposta o aggiungi alle ${time}`}
      data-testid={`week-slot-${dateISO}-${String(h).padStart(2, "0")}${String(m).padStart(2, "0")}`}
    />
  );
}

function DayColumn({ day, isToday, gridStart, gridEnd, onOpenTaskAt, filterCategory }) {
  const dateISO = ISO(day);
  const rawTasks = tasksForDate(dateISO);
  const tasks = filterCategory === "all" ? rawTasks : rawTasks.filter((t) => t.categoryId === filterCategory);
  const timed = tasks.filter((t) => t.time && !t.allDay);

  const { setNodeRef, isOver } = useDroppable({
    id: `day-col-${dateISO}`,
    data: { type: "day", date: dateISO }
  });

  const hoursCount = gridEnd - gridStart;
  const rowH = 56; // px per hour

  return (
    <div
      ref={setNodeRef}
      className={`relative border-l border-[#E4E7E1] ${isToday ? "bg-[#F3F5F0]/60" : ""} ${isOver ? "bg-[#D8E0D2]/50" : ""}`}
      data-testid={`week-col-${dateISO}`}
    >
      {/* Hour rows */}
      <div className="relative" style={{ height: hoursCount * rowH }}>
        {Array.from({ length: hoursCount }).map((_, i) => {
          const h = gridStart + i;
          return (
            <div key={h} style={{ height: rowH }} className="relative border-b border-[#E4E7E1]">
              <div className="absolute inset-x-0 top-1/2 tl-half" style={{ height: 1 }} />
              <HalfHourSlot dateISO={dateISO} h={h} m={0} onOpen={onOpenTaskAt} half="top" />
              <HalfHourSlot dateISO={dateISO} h={h} m={30} onOpen={onOpenTaskAt} half="bottom" />
            </div>
          );
        })}
        {/* Timed task blocks — laid out side-by-side when overlapping */}
        {(() => {
          const gridStartMin = gridStart * 60;
          const gridEndMin = gridEnd * 60;
          const items = timed.map((t) => {
            const s = timeToMin(t.time);
            const e = s + Math.max(15, t.durationMin || 30);
            return { task: t, startMin: s, endMin: e };
          }).filter((it) => it.startMin < gridEndMin)
            .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
          // Assign columns greedily
          const colsEnd = [];
          items.forEach((it) => {
            let col = colsEnd.findIndex((end) => end <= it.startMin);
            if (col === -1) { col = colsEnd.length; colsEnd.push(0); }
            colsEnd[col] = it.endMin;
            it.col = col;
          });
          // Cluster items via union-find on overlap
          const N = items.length;
          const parent = Array.from({ length: N }, (_, i) => i);
          const find = (x) => parent[x] === x ? x : (parent[x] = find(parent[x]));
          for (let i = 0; i < N; i++) {
            for (let j = i + 1; j < N; j++) {
              if (Math.max(items[i].startMin, items[j].startMin) < Math.min(items[i].endMin, items[j].endMin)) {
                const ra = find(i), rb = find(j);
                if (ra !== rb) parent[ra] = rb;
              }
            }
          }
          const clusterMax = {};
          items.forEach((it, i) => {
            const r = find(i);
            clusterMax[r] = Math.max(clusterMax[r] || 0, it.col + 1);
          });
          return items.map((it, i) => {
            const top = Math.max(0, (it.startMin - gridStartMin) * (rowH / 60));
            const height = Math.max(22, Math.min((gridEndMin - it.startMin), it.endMin - it.startMin) * (rowH / 60) - 2);
            const cols = clusterMax[find(i)];
            const widthPct = 100 / cols;
            const leftPct = it.col * widthPct;
            const cat = findCategory(it.task.categoryId);
            return (
              <TimedBlock
                key={`${it.task.id}-${it.task._occurrenceDate || it.task.date}`}
                task={it.task}
                category={cat}
                style={{
                  position: "absolute",
                  top,
                  height,
                  left: `calc(${leftPct}% + 2px)`,
                  width: `calc(${widthPct}% - 4px)`
                }}
              />
            );
          });
        })()}
      </div>
    </div>
  );
}

function AllDayCell({ day, filterCategory }) {
  const dateISO = ISO(day);
  const rawTasks = tasksForDate(dateISO);
  const tasks = filterCategory === "all" ? rawTasks : rawTasks.filter((t) => t.categoryId === filterCategory);
  const allDay = tasks.filter((t) => t.allDay || !t.time);
  const { setNodeRef, isOver } = useDroppable({ id: `day-allday-${dateISO}`, data: { type: "day", date: dateISO } });
  return (
    <div
      ref={setNodeRef}
      className={`border-l border-[#E4E7E1] px-1.5 py-1 flex flex-col gap-1 min-w-0 ${isOver ? "bg-[#D8E0D2]/50" : ""}`}
      style={{ minHeight: 40 }}
      data-testid={`allday-col-${dateISO}`}
    >
      {allDay.map((t) => {
        const cat = findCategory(t.categoryId);
        return (
          <TimedBlock
            key={`${t.id}-${t._occurrenceDate || t.date}`}
            task={t}
            category={cat}
            style={{ position: "relative", left: 0, right: 0, top: 0, width: "100%" }}
            compact
          />
        );
      })}
    </div>
  );
}

function TimedBlock({ task, category, style, compact = false, rowH = 56 }) {
  const [editOpen, setEditOpen] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [previewH, setPreviewH] = useState(null);
  const [pendingDur, setPendingDur] = useState(null);
  const startRef = useRef({ y: 0, dur: 0 });
  const movedRef = useRef(false);
  const timer = useStore((s) => s.timer);
  const isRunning = isTimerFor(timer, task);
  const occurrenceDate = task._occurrenceDate || task.date;
  const isRecurring = task.recurrence && task.recurrence !== "none";

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${task.id}|${occurrenceDate || "none"}`,
    data: { type: "task", task, occurrenceDate }
  });
  const s = {
    ...style,
    touchAction: "manipulation",
    ...(previewH != null ? { height: previewH } : {}),
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
    background: category.color + "26",
    borderLeft: `3px solid ${category.color}`,
    borderTop: `1px solid ${category.color}55`,
    borderRight: `1px solid ${category.color}55`,
    borderBottom: `1px solid ${category.color}55`
  };
  function onCheck(e) {
    e.stopPropagation();
    const willBeDone = !task.done;
    let intensity = 1;
    if (willBeDone && task.priority && occurrenceDate) {
      intensity = 2;
      const remainingPri = prioritiesForDate(occurrenceDate).filter((t) => !t.done).length;
      if (remainingPri === 1) intensity = 3;
    }
    toggleTaskDone(task.id, occurrenceDate);
    if (willBeDone) {
      requestAnimationFrame(() => metallicConfetti(e.currentTarget, intensity));
    }
  }
  function onBlockClick(e) {
    // Avoid opening dialog right after a drag or resize movement
    if (movedRef.current || resizing || isDragging) {
      movedRef.current = false;
      return;
    }
    e.stopPropagation();
    setEditOpen(true);
  }
  function trackMovement() {
    movedRef.current = true;
    // the click that follows a drag/resize arrives right away; later clicks must open the dialog
    window.setTimeout(() => { movedRef.current = false; }, 400);
  }
  function commitDuration(newDur) {
    if (newDur === (task.durationMin || 30)) return;
    if (isRecurring && occurrenceDate) setPendingDur(newDur);
    else updateTask(task.id, { durationMin: newDur });
  }
  function onResizeDown(e) {
    e.stopPropagation();
    const isTouch = e.pointerType === "touch";
    const startY = e.clientY;
    const startDur = task.durationMin || 30;
    let armed = !isTouch;
    let holdTimer = 0;
    // Block page scrolling only once the resize is actually armed
    const blockScroll = (ev) => { if (armed && ev.cancelable) ev.preventDefault(); };

    const arm = () => {
      armed = true;
      startRef.current = { y: startY, dur: startDur };
      setResizing(true);
    };
    if (!isTouch) {
      e.preventDefault();
      arm();
    } else {
      startRef.current = { y: startY, dur: startDur };
      // Touch: require a deliberate long press, so scrolling never resizes by accident
      holdTimer = window.setTimeout(() => {
        arm();
        if (navigator.vibrate) navigator.vibrate(15);
      }, 450);
    }

    const cleanup = () => {
      window.clearTimeout(holdTimer);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("touchmove", blockScroll);
      setResizing(false);
      setPreviewH(null);
    };
    const durFrom = (ev) => {
      const dy = ev.clientY - startRef.current.y;
      const dMin = Math.round((dy * 60) / rowH / 5) * 5; // snap 5-min
      return Math.max(5, startRef.current.dur + dMin);
    };
    const move = (ev) => {
      if (!armed) {
        // Finger moved before the long press completed → it was a scroll, not a resize
        if (Math.abs(ev.clientY - startY) > 6) cleanup();
        return;
      }
      setPreviewH(Math.max(22, durFrom(ev) * (rowH / 60) - 2));
    };
    const up = (ev) => {
      const wasArmed = armed;
      const newDur = durFrom(ev);
      cleanup();
      if (wasArmed) commitDuration(newDur);
    };
    const cancel = () => cleanup();
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("touchmove", blockScroll, { passive: false });
  }

  const { onTouchStart: dragTouchStart, ...dragListeners } = listeners || {};

  return (
    <>
    <div
      ref={setNodeRef}
      style={s}
      className={`group rounded-md pl-4 pr-1.5 md:px-1.5 py-1 text-[11px] leading-tight cursor-pointer ${compact ? "" : "overflow-hidden"} select-none ${task.done ? "opacity-60" : ""}`}
      data-testid={`week-task-${task.id}`}
      onClick={onBlockClick}
      onPointerMove={isDragging ? trackMovement : undefined}
      {...attributes}
      {...dragListeners}
    >
      {/* Mobile drag grip: keeps the rest of the block free for tapping and scrolling */}
      <div
        onTouchStart={dragTouchStart}
        onClick={(e) => e.stopPropagation()}
        className="md:hidden absolute left-0 top-0 bottom-0 w-4 flex items-center justify-center rounded-l-md"
        style={{ touchAction: "none", background: category.color + "33" }}
        data-testid={`week-grip-${task.id}`}
        aria-label="Trascina per spostare"
      >
        <GripVertical size={11} strokeWidth={1.75} color="#58635B" />
      </div>
      <div className="flex items-start gap-1 md:gap-1.5">
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onCheck}
          className="tl-check mt-0.5 flex-shrink-0"
          style={{ width: 14, height: 14 }}
          data-checked={task.done ? "true" : "false"}
          data-testid={`week-check-${task.id}`}
        >
          <Check size={8} strokeWidth={4} color="#ffffff" />
        </button>
        <span className={`${compact ? "whitespace-normal break-words" : "truncate"} flex-1 min-w-0 font-medium ${task.done ? "tl-strike" : "text-[#3B443D]"}`}>{task.title}</span>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            const r = toggleTaskPriority(task.id, occurrenceDate);
            if (!r.ok && r.reason === "limit") toast.error("Massimo 3 priorità al giorno");
          }}
          className={`hidden md:inline-flex flex-shrink-0 ${task.priority ? "text-[#D4A94A]" : "text-[#6A7368] hover:text-[#D4A94A]"}`}
          data-testid={`week-star-${task.id}`}
          aria-label="Priorità"
        >
          <Star size={11} strokeWidth={1.5} fill={task.priority ? "#D4A94A" : "none"} />
        </button>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); if (isRunning) stopTimer(); else startTimer(task.id, task._occurrenceDate || task.date); }}
          className={`hidden md:inline-flex flex-shrink-0 ${isRunning ? "text-[#8FA087]" : "text-[#6A7368] hover:text-[#8FA087]"}`}
          data-testid={`week-timer-${task.id}`}
          aria-label={isRunning ? "Pausa" : "Avvia"}
        >
          {isRunning ? <Pause size={11} strokeWidth={1.5} /> : <Play size={11} strokeWidth={1.5} />}
        </button>
      </div>
      {!compact && task.time && (
        <div className="text-[10px] text-[#58635B] mt-0.5 opacity-80">{task.time}{task.durationMin ? ` · ${task.durationMin} min` : ""}</div>
      )}
      {!compact && (
        <div
          onPointerDown={onResizeDown}
          onTouchStart={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-3 rounded-t-md md:translate-x-0 md:left-0 md:right-0 md:w-auto md:h-2 md:rounded-none flex items-end justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 cursor-ns-resize md:[background:var(--tl-grad)] ${resizing ? "ring-2 ring-[#8FA087]" : ""}`}
          style={{ touchAction: "none", "--tl-grad": `linear-gradient(to bottom, transparent, ${category.color}88)` }}
          data-testid={`week-resize-${task.id}`}
          aria-label="Ridimensiona"
        >
          <span className="md:hidden mb-0.5 block w-7 h-[3px] rounded-full" style={{ background: category.color }} />
        </div>
      )}
    </div>
    <TaskDialog open={editOpen} onClose={() => setEditOpen(false)} task={task} />
    <RecurrenceScopeDialog
      open={pendingDur != null}
      label="Cambiare la durata di..."
      onCancel={() => setPendingDur(null)}
      onChoose={(scope) => {
        if (scope === "single") updateOccurrence(task.id, occurrenceDate, { durationMin: pendingDur });
        else updateTask(task.id, { durationMin: pendingDur });
        setPendingDur(null);
      }}
    />
    </>
  );
}

export default function WeekGrid({ weekStart, gridStart = 6, gridEnd = 23, onOpenTaskAt, filterCategory = "all" }) {
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const todayISO = ISO(new Date());
  const rowH = 56;
  // Mobile: fixed-time-column 44px + 7 day columns of ~30vw (so 3 fit + a peek of the 4th).
  // Desktop: 72px + evenly-distributed remaining space.
  const gridCols = "[grid-template-columns:44px_repeat(7,30vw)] md:[grid-template-columns:72px_repeat(7,1fr)]";

  return (
    <div className="tl-paper [overflow:clip] min-w-max md:min-w-0">
      {/* Sticky block: day header + all-day row stay visible while scrolling vertically */}
      <div className="sticky top-0 z-30">
      {/* Header row */}
      <div className={`grid ${gridCols}`}>
        <div className="p-3 border-b border-r border-[#E4E7E1] bg-[#A3B897] sticky left-0 z-40" />
        {days.map((d) => {
          const isToday = ISO(d) === todayISO;
          return (
            <div key={d.toISOString()} className={`tl-week-day-cell px-1 py-1.5 md:p-3 border-b border-l border-[#8FA087] text-center ${isToday ? "bg-[#8FA087]" : "bg-[#A3B897]"}`}>
              {/* Mobile: weekday name + number on one compact line, so the name is always readable */}
              <div className="md:hidden flex items-baseline justify-center gap-1 whitespace-nowrap">
                <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/90">{fmtWeekday(d)}</span>
                <span className="font-serif-display font-bold text-[18px] leading-none text-white">{fmtDayNum(d)}</span>
              </div>
              <div className="hidden md:block">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90">{fmtWeekday(d)}</div>
                <div className="font-serif-display font-bold text-[28px] leading-none mt-1 text-white">
                  {fmtDayNum(d)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* All-day row — shared height across columns via CSS grid */}
      <div className={`grid border-b border-[#E4E7E1] items-stretch ${gridCols}`}>
        <div className="border-r border-[#E4E7E1] flex items-center justify-center px-0.5 py-1 bg-white sticky left-0 z-40">
          <span className="md:hidden text-[8px] font-semibold text-[#6A7368] text-center w-full leading-[1.2] normal-case">
            tutto<br />il giorno
          </span>
          <span className="tl-label hidden md:block text-[10px] text-center leading-tight">
            tutto il giorno
          </span>
        </div>
        {days.map((d) => (
          <AllDayCell key={d.toISOString()} day={d} filterCategory={filterCategory} />
        ))}
      </div>
      </div>
      {/* Body: time column + day columns */}
      <div className={`grid ${gridCols}`}>
        {/* Time column */}
        <div className="border-r border-[#E4E7E1] bg-white sticky left-0 z-20" data-testid="week-time-gutter">
          <div>
            {Array.from({ length: gridEnd - gridStart }).map((_, i) => {
              const h = gridStart + i;
              return (
                <div key={h} style={{ height: rowH }} className="border-b border-[#E4E7E1] px-1 md:px-2 pt-1 text-[10px] md:text-[11px] text-[#6A7368] tabular-nums">
                  {String(h).padStart(2, "0")}:00
                </div>
              );
            })}
          </div>
        </div>
        {days.map((d) => (
          <DayColumn
            key={d.toISOString()}
            day={d}
            isToday={ISO(d) === todayISO}
            gridStart={gridStart}
            gridEnd={gridEnd}
            onOpenTaskAt={onOpenTaskAt}
            filterCategory={filterCategory}
          />
        ))}
      </div>
    </div>
  );
}
